import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { TransferMethod } from '@/types/schema';

import {
  BeneficiaryDesignationsFormNamespace,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';

interface ToggleTransferInKindMenuItemProps {
  /** Field path prefix before `.recipients.${index}.transferMethod` */
  recipientsBasePath: BeneficiaryDesignationsFormNamespace;
}

/**
 * Bulk-sets every recipient's transfer method for a tier. Unlike the DP form
 * (which stores a single form-level transfer method), beneficiary designations
 * carry transfer method per provision, so this toggles each recipient.
 */
export function ToggleTransferInKindMenuItem({
  recipientsBasePath,
}: ToggleTransferInKindMenuItemProps) {
  const { control, setValue } =
    useFormContext<BeneficiaryDesignationsFormShape>();

  const recipients = useWatch({
    control,
    name: `${recipientsBasePath}.recipients`,
    defaultValue: [],
  });

  const allInKind = useMemo(
    () =>
      recipients.length > 0 &&
      recipients.every((r) => r.transferMethod === TransferMethod.InKind),
    [recipients]
  );

  const handleToggle = useCallback(() => {
    const newMethod = allInKind
      ? TransferMethod.LiquidateToCash
      : TransferMethod.InKind;
    recipients.forEach((_r, index) => {
      setValue(
        `${recipientsBasePath}.recipients.${index}.transferMethod`,
        newMethod,
        { shouldDirty: true }
      );
    });
  }, [allInKind, recipients, recipientsBasePath, setValue]);

  if (recipients.length === 0) {
    return null;
  }

  return (
    <MenuItem
      key="transfer-in-kind-toggle"
      label={
        <Stack direction="row" alignItems="center" spacing={1}>
          <StyledCheckbox onChange={handleToggle} value={allInKind} />
          <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>
            Transfer assets in kind
          </Typography>
        </Stack>
      }
      onClick={(event) => {
        event.stopPropagation();
      }}
    />
  );
}
